import { Link } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useConversations } from '@/hooks/useMarketplaceMessages';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

export function MessageBell() {
  const { user } = useAuth();
  const { data: conversations } = useConversations();
  
  // Count unread admin messages
  const { data: unreadAdminMessages } = useQuery({
    queryKey: ['unread-admin-messages', user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;
      
      const { count, error } = await supabase
        .from('admin_user_messages')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .is('read_at', null);
      
      if (error) return 0;
      return count || 0;
    },
    enabled: !!user?.id,
  });
  
  // Calculate total unread from conversations
  const unreadConversations = conversations?.reduce((acc, conv) => acc + (conv.unread_count || 0), 0) || 0;
  
  // Total unread messages
  const totalUnread = unreadConversations + (unreadAdminMessages || 0);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="icon" className="relative" asChild>
            <Link to="/marketplace/messages">
              <MessageCircle className="h-5 w-5" />
              {totalUnread > 0 && (
                <Badge 
                  className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs gradient-primary text-primary-foreground border-0"
                >
                  {totalUnread > 9 ? '9+' : totalUnread}
                </Badge>
              )}
            </Link>
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>Messages{totalUnread > 0 ? ` (${totalUnread} unread)` : ''}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
